import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  DollarSign,
  Share2,
  Users,
  Lock,
  Landmark,
  BarChart,
  Calendar,
  ArrowUpRight,
  Plus,
  Coins
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface Collaborator {
  id: string;
  name: string;
  role: string;
  splitPercentage: number;
  walletAddress?: string;
}

const RoyaltyDistribution: React.FC = () => {
  const { toast } = useToast();
  const [collaborators, setCollaborators] = useState<Collaborator[]>([
    { id: '1', name: 'You (Primary Artist)', role: 'Artist', splitPercentage: 70, walletAddress: '0x4f2e...a8c3' },
    { id: '2', name: 'John Doe', role: 'Producer', splitPercentage: 20 },
    { id: '3', name: 'Jane Smith', role: 'Songwriter', splitPercentage: 10 }
  ]);
  
  const [newCollaborator, setNewCollaborator] = useState({
    name: '',
    role: '',
    splitPercentage: 0,
    walletAddress: ''
  });
  
  const [selectedReleaseId, setSelectedReleaseId] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  
  const totalSplit = collaborators.reduce((sum, collab) => sum + collab.splitPercentage, 0);
  const isValidSplit = Math.abs(totalSplit - 100) < 0.01; // Allow small floating-point errors
  
  const handleSliderChange = (id: string, value: number[]) => {
    setCollaborators(collaborators.map(collab => 
      collab.id === id ? { ...collab, splitPercentage: value[0] } : collab
    ));
  };
  
  const handleAddCollaborator = () => {
    if (!newCollaborator.name || !newCollaborator.role) {
      toast({
        title: "Missing information",
        description: "Please provide a name and role for the collaborator",
        variant: "destructive",
      });
      return;
    }
    
    const remainingPercentage = 100 - totalSplit;
    if (remainingPercentage <= 0) {
      toast({
        title: "Split percentage error",
        description: "Total split already equals or exceeds 100%. Adjust existing splits first.",
        variant: "destructive",
      });
      return;
    }
    
    const adjustedSplit = newCollaborator.splitPercentage > remainingPercentage 
      ? remainingPercentage 
      : newCollaborator.splitPercentage;
    
    setCollaborators([
      ...collaborators,
      {
        id: `${collaborators.length + 1}`,
        name: newCollaborator.name,
        role: newCollaborator.role,
        splitPercentage: adjustedSplit,
        walletAddress: newCollaborator.walletAddress || undefined
      }
    ]);
    
    setNewCollaborator({
      name: '',
      role: '',
      splitPercentage: 0,
      walletAddress: ''
    });
    
    setShowAddForm(false);
    
    toast({
      title: "Collaborator added",
      description: `${newCollaborator.name} has been added with a ${adjustedSplit}% split`,
    });
  };
  
  const handleDeploySmartContract = () => {
    if (!isValidSplit) {
      toast({
        title: "Invalid split percentages",
        description: "Split percentages must total exactly 100%",
        variant: "destructive",
      });
      return;
    }
    
    const missingWallets = collaborators.some(collab => !collab.walletAddress);
    if (missingWallets) {
      toast({
        title: "Missing wallet addresses",
        description: "All collaborators must have a wallet address before deploying",
        variant: "destructive",
      });
      return;
    }
    
    toast({
      title: "Smart contract deployment initiated",
      description: "Your royalty distribution contract is being deployed to the blockchain",
    });
    
    // Simulate deployment
    setTimeout(() => {
      toast({
        title: "Smart contract deployed",
        description: "Your royalty distribution is now managed on the blockchain",
      });
    }, 3000);
  };
  
  return (
    <Card variant="gradient" className="overflow-hidden">
      <CardHeader>
        <CardTitle className="text-xl flex items-center gap-2 text-white">
          <Coins className="h-5 w-5 text-white/80" />
          Royalty Distribution
        </CardTitle>
        <CardDescription className="text-white/80">
          Manage royalty splits with collaborators using blockchain smart contracts
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-white">Collaborator Splits</h3>
            <Button 
              variant="glass" 
              size="sm" 
              className="gap-1"
              onClick={() => setShowAddForm(!showAddForm)}
            >
              <Plus className="h-3 w-3" />
              Add Collaborator
            </Button>
          </div>
          
          {!isValidSplit && (
            <div className="bg-white/10 border border-white/20 text-white p-2 rounded text-sm backdrop-blur-sm">
              Total split: {totalSplit}% (must equal 100%)
            </div>
          )}
          
          {showAddForm && (
            <div className="border border-white/20 rounded-md p-3 space-y-3 bg-white/10 backdrop-blur-sm">
              <h4 className="text-sm font-medium text-white">Add New Collaborator</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="name" className="text-white/90">Name</Label>
                  <Input 
                    id="name" 
                    value={newCollaborator.name}
                    onChange={e => setNewCollaborator({...newCollaborator, name: e.target.value})}
                    placeholder="Collaborator name"
                    className="bg-white/20 border-white/10 text-white placeholder:text-white/50"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="role" className="text-white/90">Role</Label>
                  <Input 
                    id="role" 
                    value={newCollaborator.role}
                    onChange={e => setNewCollaborator({...newCollaborator, role: e.target.value})}
                    placeholder="Producer, Songwriter, etc."
                    className="bg-white/20 border-white/10 text-white placeholder:text-white/50"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="split" className="text-white/90">Split Percentage</Label>
                  <div className="flex items-center gap-2">
                    <Slider
                      id="split"
                      value={[newCollaborator.splitPercentage]}
                      max={100}
                      step={1}
                      onValueChange={(value) => setNewCollaborator({...newCollaborator, splitPercentage: value[0]})}
                      className="flex-1"
                    />
                    <span className="w-10 text-right text-white">{newCollaborator.splitPercentage}%</span>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="wallet" className="text-white/90">Wallet Address (Optional)</Label>
                  <Input 
                    id="wallet" 
                    value={newCollaborator.walletAddress}
                    onChange={e => setNewCollaborator({...newCollaborator, walletAddress: e.target.value})}
                    placeholder="0x..."
                    className="bg-white/20 border-white/10 text-white placeholder:text-white/50"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <Button variant="glass" size="sm" onClick={() => setShowAddForm(false)}>
                  Cancel
                </Button>
                <Button variant="glass" size="sm" onClick={handleAddCollaborator}>
                  Add Collaborator
                </Button>
              </div>
            </div>
          )}
          
          <div className="space-y-3">
            {collaborators.map((collaborator) => (
              <div key={collaborator.id} className="border border-white/20 rounded-md p-3 bg-white/10 backdrop-blur-sm">
                <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <div className="font-medium text-white">{collaborator.name}</div>
                      <div className="text-sm text-white/70">{collaborator.role}</div>
                    </div>
                    {collaborator.walletAddress ? (
                      <div className="text-xs text-white/60 font-mono truncate">
                        {collaborator.walletAddress}
                      </div>
                    ) : (
                      <Button variant="glass" size="sm" className="text-xs px-2 h-6">
                        <Plus className="h-3 w-3 mr-1" />
                        Add wallet address
                      </Button>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Slider
                      value={[collaborator.splitPercentage]}
                      max={100}
                      step={1}
                      onValueChange={(value) => handleSliderChange(collaborator.id, value)}
                      className="w-32 md:w-40"
                    />
                    <span className="w-10 text-right text-white">{collaborator.splitPercentage}%</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="bg-white/10 rounded-lg p-4 space-y-3 border border-white/20 backdrop-blur-sm">
          <h3 className="text-sm font-medium flex items-center gap-1 text-white">
            <Lock className="h-4 w-4" />
            Smart Contract Information
          </h3>
          <p className="text-sm text-white/80">
            Deploying a smart contract will create an immutable record of royalty splits on the blockchain. 
            All future earnings will be automatically distributed according to these percentages.
          </p>
          <div className="pt-2">
            <Button 
              className="gap-2 w-full sm:w-auto"
              variant="neon"
              onClick={handleDeploySmartContract}
              disabled={!isValidSplit}
            >
              <Landmark className="h-4 w-4" />
              Deploy Smart Contract
            </Button>
          </div>
        </div>
      </CardContent>
      <CardFooter className="bg-white/5 backdrop-blur-sm px-4 py-3 flex items-center justify-between text-xs text-white/60 border-t border-white/10">
        <div className="flex items-center gap-1">
          <Calendar className="h-3 w-3" />
          <span>Last updated: Today</span>
        </div>
        <Button variant="link" size="sm" className="gap-1 text-xs h-auto py-0 text-white/80 hover:text-white">
          View transaction history
          <ArrowUpRight className="h-3 w-3" />
        </Button>
      </CardFooter>
    </Card>
  );
};

export default RoyaltyDistribution;
